// Хук для управления push-уведомлениями
import { useState, useEffect, useCallback } from 'react';

// VAPID публичный ключ (должен совпадать с сервером)
const VAPID_PUBLIC_KEY = 'BF09L0o5kRcr1fijpGg4Md6ZlX_eDVNHU8GSggZkcwJSYlzQOU3RWB9dk7etApTphwiONN1PpT902RJGtMNWydQ';

interface PushSubscriptionState {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  permission: NotificationPermission;
}

export const usePushNotifications = () => {
  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    isSubscribed: false,
    isLoading: false,
    error: null,
    permission: 'default'
  });

  // Проверка поддержки push-уведомлений
  const checkSupport = useCallback(() => {
    const isSupported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setState(prev => ({
      ...prev,
      isSupported,
      permission: Notification.permission
    }));

    return isSupported;
  }, []);

  // Регистрация Service Worker
  const registerServiceWorker = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker не поддерживается');
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker зарегистрирован:', registration);

      // Ждем активации Service Worker
      if (registration.installing) {
        await new Promise((resolve) => {
          const worker = registration.installing;
          worker?.addEventListener('statechange', () => {
            if (worker.state === 'activated') {
              resolve(registration);
            }
          });
        });
      }

      return registration;
    } catch (error) {
      console.error('Ошибка регистрации Service Worker:', error);
      setState(prev => ({ ...prev, error: `Ошибка Service Worker: ${error}` }));
      return null;
    }
  }, []);

  // Запрос разрешения на уведомления
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      if (!checkSupport()) {
        throw new Error('Push-уведомления не поддерживаются');
      }

      const permission = await Notification.requestPermission();
      setState(prev => ({ ...prev, permission }));

      if (permission !== 'granted') {
        throw new Error('Разрешение на уведомления не предоставлено');
      }

      return true;
    } catch (error) {
      console.error('Ошибка запроса разрешения:', error);
      setState(prev => ({ ...prev, error: `Ошибка разрешения: ${error}` }));
      return false;
    }
  }, [checkSupport]);

  // Подписка на push-уведомления
  const subscribe = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Проверяем поддержку
      if (!checkSupport()) {
        throw new Error('Push-уведомления не поддерживаются');
      }

      // Запрашиваем разрешение
      const hasPermission = await requestPermission();
      if (!hasPermission) {
        throw new Error('Нет разрешения на уведомления');
      }

      // Регистрируем Service Worker
      const registration = await registerServiceWorker();
      if (!registration) {
        throw new Error('Не удалось зарегистрировать Service Worker');
      }

      // Создаем подписку
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      console.log('Push подписка создана:', subscription);

      // Отправляем подписку на сервер
      const username = localStorage.getItem('username');
      const guid = localStorage.getItem('guid');

      if (!username || !guid) {
        throw new Error('Данные пользователя не найдены');
      }

      // Используем текущий origin (nginx будет обрабатывать SSL)
      const docVersion = process.env.REACT_APP_DOC_VERSION || '1.0.6';
      const apiUrl = `${window.location.origin}/api/v1/save-subscription?version=${docVersion}`;
      console.log('Отправка подписки на:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-app-version': docVersion
        },
        body: JSON.stringify({
          user_id: username,
          user_guid: guid,
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка сохранения подписки');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Ошибка сохранения подписки');
      }

      setState(prev => ({
        ...prev,
        isSubscribed: true,
        isLoading: false,
        error: null
      }));

      return true;

    } catch (error) {
      console.error('Ошибка подписки:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Ошибка подписки: ${error}`
      }));
      return false;
    }
  }, [checkSupport, requestPermission, registerServiceWorker]);

  // Проверка существующей подписки
  const checkExistingSubscription = useCallback(async () => {
    try {
      if (!checkSupport()) return;

      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();
      setState(prev => ({
        ...prev,
        isSubscribed: !!subscription
      }));
    } catch (error) {
      console.error('Ошибка проверки подписки:', error);
    }
  }, [checkSupport]);

  // Отписка от push-уведомлений
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        throw new Error('Service Worker не найден');
      }

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        throw new Error('Подписка не найдена');
      }

      await subscription.unsubscribe();

      setState(prev => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        error: null
      }));

      return true;
    } catch (error) {
      console.error('Ошибка отписки:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Ошибка отписки: ${error}`
      }));
      return false;
    }
  }, []);

  // Инициализация при монтировании
  useEffect(() => {
    checkSupport();
    checkExistingSubscription();
  }, [checkSupport, checkExistingSubscription]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    requestPermission,
    checkSupport
  };
};

// Утилита для конвертации VAPID ключа
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
